/**
 * AdminLayout — shell compartida para todas las páginas /admin y /dashboard.
 *
 * Design language: "Obsidian Glass" + iOS materials
 *   • Sidebar negro puro + chrome header con blur/saturate
 *   • Dock inferior tipo tab bar (mobile)
 *   • Active nav: glass pill + soft gradient
 *   • Press feedback (scale 0.97) en controles táctiles
 *   • Avatar Cloudinary con crop facial (g_face) del banner
 *
 * Stack: shadcn/Radix + Tailwind dark. Sin framework externo de UI.
 */

import { UserButton, useAuth } from "@clerk/clerk-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  loadSidebarOrder,
  saveSidebarOrder,
  clearSidebarOrder,
  sortGroupItems,
  type SidebarOrderMap,
} from "@/lib/adminSidebarOrderStorage";
import {
  ADMIN_SIDEBAR_NAV_GROUPS,
  type AdminSidebarNavGroup,
  type AdminSidebarNavItem,
} from "@/components/admin/adminSidebarNav";
import { useIsSuperAdmin, useIsRevenueOnlyAdmin } from "@/lib/auth";
import { useAdminSidebarConfig } from "@/hooks/useAdminSidebarConfig";
import {
  REVENUE_ONLY_ADMIN_HOME,
  isPathAllowedForRevenueOnlyAdmin,
} from "@/lib/adminNavVisibility";
import {
  EMPTY_PENDING_COUNTS,
  PendingCountsContext,
  pendingCountForKey,
  usePendingCounts,
  usePendingCountsQuery,
} from "@/hooks/usePendingCounts";
import { filterVisibleAdminNavGroups } from "@/lib/adminNavVisibility";
import { isAdminNavItemActive } from "@/lib/adminRouteMatch";
import { AdminCommandPalette } from "@/components/admin/AdminCommandPalette";
import { BRAND_PERSON_NAME, BRAND_SITE_URL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { useSEO } from "@/hooks/useSEO";
import { HeroUIProvider } from "@heroui/react";
import { ChevronDown, Clock, Command, ExternalLink, GripVertical, PanelLeft, RotateCcw, Search, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

// Avatar generado desde el banner de Cloudinary con crop facial automático
const CLOUDINARY_AVATAR_URL =
  "https://res.cloudinary.com/nm-creators/image/upload/w_56,h_56,c_fill,g_face,r_max,f_auto,q_auto/v1779993658/header-banner-juaperrone_rxgxxd.png";

const ADMIN_SEO = {
  title: `Admin - ${BRAND_PERSON_NAME}`,
  description: "Panel de administración",
  keywords: "",
  ogImage: BRAND_OG_IMAGE_URL,
  canonical: `${BRAND_SITE_URL}/admin`,
  robots: "noindex, nofollow" as const,
};

function readSidebarCookie(): boolean {
  if (typeof document === "undefined") return true;
  const match = document.cookie.match(/(?:^|;\s*)sidebar:state=([^;]+)/);
  return match ? match[1] === "true" : true;
}

function useIsItemActive(item: AdminSidebarNavItem): boolean {
  const { pathname, search, hash } = useLocation();
  return isAdminNavItemActive(item.to, !!item.end, pathname, search, hash);
}

function matchesSidebarQuery(item: AdminSidebarNavItem, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (item.label.toLowerCase().includes(q)) return true;
  if (item.hint?.toLowerCase().includes(q)) return true;
  return (item.children ?? []).some((child) => child.label.toLowerCase().includes(q));
}

// ─── Nav: item con hijos ──────────────────────────────────────────────────────

function NavItemWithChildren({
  item,
  onNavigate,
}: {
  item: AdminSidebarNavItem & {
    children: NonNullable<AdminSidebarNavItem["children"]>;
  };
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const isGroupActive =
    isAdminNavItemActive(
      item.to,
      false,
      location.pathname,
      location.search,
      location.hash,
    ) ||
    (item.children?.some((child) =>
      isAdminNavItemActive(
        child.to,
        true,
        location.pathname,
        location.search,
        location.hash,
      ),
    ) ??
      false);
  const [open, setOpen] = useState(isGroupActive);

  const handleNavigate = () => {
    onNavigate?.();
    if (isMobile) setOpenMobile(false);
  };

  useEffect(() => {
    if (isGroupActive) setOpen(true);
  }, [isGroupActive]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.label}
            isActive={isGroupActive}
            className={cn(
              "ios-press h-9 rounded-xl transition-[background-color,color,transform,box-shadow] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] md:h-8",
              isGroupActive
                ? "admin-linear-outline bg-gradient-to-r from-white/[0.16] to-white/[0.06] text-white shadow-sm"
                : "text-zinc-400 hover:bg-white/[0.07] hover:text-zinc-100",
            )}
          >
            <item.icon aria-hidden className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-[13px] font-medium">{item.label}</span>
            <span className="admin-linear-pill hidden px-1.5 py-0 text-[10px] text-zinc-400 group-data-[collapsible=icon]:hidden sm:inline-flex">
              {item.children.length}
            </span>
            <ChevronDown
              className={cn(
                "h-3 w-3 shrink-0 transition-transform duration-200",
                open ? "rotate-180 text-zinc-400" : "text-zinc-600",
              )}
              aria-hidden
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="ml-3 border-l border-white/[0.06] pl-3">
            {item.children.map((child) => {
              const isChildActive = isAdminNavItemActive(
                child.to,
                true,
                location.pathname,
                location.search,
                location.hash,
              );
              return (
                <SidebarMenuSubItem key={child.to}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isChildActive}
                    className={cn(
                      "ios-press h-8 rounded-lg text-[12.5px] transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]",
                      isChildActive
                        ? "admin-linear-outline bg-white/[0.09] text-white"
                        : "text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200",
                    )}
                  >
                    <NavLink to={child.to} onClick={handleNavigate}>
                      <child.icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
                      <span>{child.label}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

// ─── Nav: fila reordenable (drag & drop) ─────────────────────────────────────

/**
 * Wrapper sortable de un ítem del nav — patrón SortableSidebarNavSection de
 * clarimoine, adaptado: el grip aparece en hover (desktop expandido) y todo
 * el reorden persiste en localStorage por usuario Clerk.
 */
function SortableNavRow({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group/sortable relative", isDragging && "z-[100]")}
    >
      {!disabled && (
        <button
          type="button"
          className="absolute -left-1.5 top-1/2 z-10 hidden h-6 w-4 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded text-zinc-700 opacity-0 transition-opacity duration-150 hover:text-zinc-400 active:cursor-grabbing group-hover/sortable:opacity-100 group-data-[collapsible=icon]:!hidden md:flex"
          aria-label="Arrastrar para reordenar el menú"
          title="Arrastrar para reordenar (solo vos, este navegador)"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}
      {children}
    </div>
  );
}

// ─── Nav: badge de pendientes ─────────────────────────────────────────────────

/**
 * Count ámbar de trabajo pendiente (aprobaciones esperando). Patrón portado
 * del PendingUsersIndicator de clarimoine — acá inline en el ítem del nav
 * para que el pendiente sea visible sin entrar a la página.
 */
function NavPendingBadge({ item }: { item: AdminSidebarNavItem }) {
  const counts = usePendingCounts();
  const count = pendingCountForKey(counts, item.pendingKey);
  if (count === 0) return null;
  return (
    <span
      className="ml-auto inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 text-[10px] font-semibold tabular-nums text-amber-300 shadow-[0_0_8px_rgba(245,158,11,0.25)] group-data-[collapsible=icon]:hidden"
      aria-label={`${count} pendiente${count !== 1 ? "s" : ""}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

// ─── Nav: item hoja ───────────────────────────────────────────────────────────

function NavItemLeaf({
  item,
  onNavigate,
}: {
  item: AdminSidebarNavItem;
  onNavigate?: () => void;
}) {
  const isActive = useIsItemActive(item);
  const { isMobile, setOpenMobile } = useSidebar();

  const handleNavigate = () => {
    onNavigate?.();
    if (isMobile) setOpenMobile(false);
  };

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.label}
        isActive={isActive}
        className={cn(
          "ios-press h-9 rounded-xl transition-[background-color,color,transform,box-shadow] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] md:h-8",
          isActive
            ? "admin-linear-outline bg-gradient-to-r from-white/[0.16] to-white/[0.06] text-white shadow-sm"
            : "text-zinc-400 hover:bg-white/[0.07] hover:text-zinc-100",
        )}
      >
        <NavLink to={item.to} end={item.end} onClick={handleNavigate}>
          <item.icon aria-hidden className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{item.label}</span>
          {item.pendingKey && <NavPendingBadge item={item} />}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ─── Breadcrumb trail ─────────────────────────────────────────────────────────

function useBreadcrumbTrail() {
  const { pathname, search, hash } = useLocation();
  return useMemo(() => {
    for (const group of ADMIN_SIDEBAR_NAV_GROUPS) {
      for (const item of group.items) {
        const child = item.children?.find((c) =>
          isAdminNavItemActive(c.to, true, pathname, search, hash),
        );
        if (child) {
          return {
            section: group.label,
            page: item.label,
            sub: child.label,
          };
        }
        if (isAdminNavItemActive(item.to, !!item.end, pathname, search, hash)) {
          return {
            section: group.label,
            page: item.label,
            sub: null,
          };
        }
      }
    }
    return { section: "Admin", page: "Panel", sub: null };
  }, [pathname, search, hash]);
}

// ─── Header glass ─────────────────────────────────────────────────────────────

/** Chip del header: atajo directo a la cola de aprobaciones cuando hay pendientes. */
function HeaderPendingChip() {
  const counts = usePendingCounts();
  if (counts.total === 0) return null;
  return (
    <NavLink
      to="/admin/memberships"
      className="ios-press hidden items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-300 transition-colors hover:bg-amber-500/20 sm:inline-flex"
      title="Aprobaciones esperando acción"
    >
      <Clock className="h-3 w-3 animate-pulse" aria-hidden />
      <span className="tabular-nums">{counts.total}</span>
      <span>pendiente{counts.total !== 1 ? "s" : ""}</span>
    </NavLink>
  );
}

function AdminHeader({ onOpenPalette }: { onOpenPalette: () => void }) {
  const trail = useBreadcrumbTrail();
  return (
    <header className="admin-chrome-bar sticky top-0 z-30 flex h-[calc(3.35rem+env(safe-area-inset-top,0px))] shrink-0 items-center gap-2 px-2.5 pt-[env(safe-area-inset-top,0px)] sm:h-13 sm:px-4 sm:pt-0">
      <SidebarTrigger className="ios-press h-9 w-9 rounded-xl text-zinc-300 hover:bg-white/[0.07] hover:text-white sm:h-8 sm:w-8 sm:text-zinc-400" />
      <Separator orientation="vertical" className="mr-1 h-4 bg-white/[0.08]" />
      <span className="admin-linear-pill hidden px-2 text-[10px] uppercase tracking-[0.16em] text-zinc-400 sm:inline-flex">
        <Sparkles className="h-3 w-3" />
        Admin
      </span>

      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="flex-nowrap gap-1">
          <BreadcrumbItem className="hidden sm:inline-flex">
            <span className="text-[12px] text-zinc-500">{trail.section}</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden text-zinc-600 sm:block" />
          <BreadcrumbItem className="min-w-0">
            <BreadcrumbPage className="truncate font-ios text-[13px] font-semibold tracking-tight text-zinc-100">
              {trail.page}
            </BreadcrumbPage>
          </BreadcrumbItem>
          {trail.sub && (
            <>
              <BreadcrumbSeparator className="text-zinc-700" />
              <BreadcrumbItem className="min-w-0">
                <BreadcrumbPage className="truncate text-[13px] text-zinc-500">
                  {trail.sub}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-2">
        <HeaderPendingChip />
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenPalette}
          className="ios-press h-9 gap-1.5 rounded-xl border border-white/[0.08] bg-white/[0.05] px-2.5 text-[12px] text-zinc-300 hover:bg-white/[0.09] hover:text-white sm:h-8 sm:text-zinc-300"
        >
          <Command className="h-3.5 w-3.5 sm:hidden" />
          <Search className="hidden h-3.5 w-3.5 sm:block" />
          <span className="hidden sm:inline">Buscar</span>
          <kbd className="hidden rounded-md border border-white/[0.1] bg-white/[0.06] px-1.5 font-mono text-[10px] leading-5 text-zinc-500 sm:inline">
            ⌘K
          </kbd>
        </Button>
      </div>
    </header>
  );
}

function AdminMobileDock({
  items,
  onOpenPalette,
}: {
  items: readonly AdminSidebarNavItem[];
  onOpenPalette: () => void;
}) {
  const location = useLocation();
  const { toggleSidebar, openMobile } = useSidebar();
  const dockItems = items.slice(0, 4);
  if (dockItems.length === 0) return null;

  return (
    <nav
      className={cn(
        "fixed inset-x-0 bottom-[max(0.55rem,env(safe-area-inset-bottom,0px))] z-40 px-3 transition-opacity duration-200 md:hidden",
        openMobile && "pointer-events-none opacity-0",
      )}
      aria-label="Navegación rápida admin"
    >
      <div className="admin-dock-surface flex items-center justify-between gap-1 rounded-[1.35rem] px-2 py-1.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="ios-press h-10 w-10 rounded-2xl text-zinc-300"
          aria-label="Abrir menú"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
        {dockItems.map((item) => {
          const active = isAdminNavItemActive(
            item.to,
            !!item.end,
            location.pathname,
            location.search,
            location.hash,
          );
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                "ios-press flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-1.5 text-[10px] font-medium transition-colors",
                active
                  ? "bg-white/[0.14] text-white shadow-sm shadow-black/20"
                  : "text-zinc-500",
              )}
            >
              <item.icon className={cn("h-4 w-4 shrink-0", active && "text-white")} />
              <span className="max-w-full truncate">{item.label}</span>
            </NavLink>
          );
        })}
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenPalette}
          className="ios-press h-10 w-10 rounded-2xl text-zinc-300"
          aria-label="Abrir comandos"
        >
          <Command className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function AdminLayout() {
  useSEO(ADMIN_SEO);
  const navigate = useNavigate();
  const [defaultOpen] = useState(readSidebarCookie);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarQuery, setSidebarQuery] = useState("");
  const [avatarError, setAvatarError] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();
  const isRevenueOnlyAdmin = useIsRevenueOnlyAdmin();
  const { hidden } = useAdminSidebarConfig();
  const { userId: clerkUserId } = useAuth();

  // ── Orden personalizado del sidebar (drag & drop, localStorage por usuario) ──
  const [orderMap, setOrderMap] = useState<SidebarOrderMap | null>(null);
  useEffect(() => {
    setOrderMap(loadSidebarOrder(clerkUserId));
  }, [clerkUserId]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleGroupDragEnd = (groupLabel: string, orderedPaths: string[]) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !clerkUserId) return;
    const oldIndex = orderedPaths.indexOf(String(active.id));
    const newIndex = orderedPaths.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next: SidebarOrderMap = {
      ...(orderMap ?? {}),
      [groupLabel]: arrayMove(orderedPaths, oldIndex, newIndex),
    };
    setOrderMap(next);
    saveSidebarOrder(clerkUserId, next);
  };

  const resetSidebarOrder = () => {
    if (clerkUserId) clearSidebarOrder(clerkUserId);
    setOrderMap(null);
  };

  const hasCustomOrder = Boolean(orderMap && Object.keys(orderMap).length > 0);

  // Flag the body so that HeroUI portals (modals, dropdowns, toasts), Radix
  // dialogs, cmdk command palette, and Sonner toasts — all of which mount at
  // document.body via React Portal, outside .admin-app-shell — pick up the SF
  // Pro stack via the `body.admin-portal-active` and `body.admin-context` rules
  // in src/index.css. Both classes are toggled together for backwards-compat
  // with the original ruleset and the user-requested `admin-context` selector.
  useEffect(() => {
    document.body.classList.add("admin-portal-active", "admin-context");
    return () => {
      document.body.classList.remove("admin-portal-active", "admin-context");
    };
  }, []);

  const visibilityFilteredGroups: readonly AdminSidebarNavGroup[] = filterVisibleAdminNavGroups({
    isSuperAdmin,
    hidden,
    isRevenueOnlyAdmin,
  });

  // Orden guardado primero, después el filtro de búsqueda. El DnD se
  // deshabilita mientras hay query activa (los índices filtrados no mapean).
  const filteredGroups: readonly AdminSidebarNavGroup[] = visibilityFilteredGroups
    .map((group) => ({
      ...group,
      items: sortGroupItems(group.label, group.items, orderMap).filter((item) =>
        matchesSidebarQuery(item, sidebarQuery),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const dndDisabled = sidebarQuery.trim().length > 0 || !clerkUserId;

  const mobileDockItems = useMemo(() => {
    const flat = visibilityFilteredGroups.flatMap((group) => group.items);
    const preferred = [
      "/admin",
      "/admin/dual-cta-variants",
      "/admin/memberships",
      "/admin/preview/dual",
      "/admin/revenue",
    ];
    const prioritized = preferred
      .map((to) => flat.find((item) => item.to === to))
      .filter((item): item is AdminSidebarNavItem => Boolean(item));
    return prioritized.length >= 3 ? prioritized : flat.slice(0, 4);
  }, [visibilityFilteredGroups]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const initial = (BRAND_PERSON_NAME || "A").charAt(0).toUpperCase();
  const totalVisibleModules = visibilityFilteredGroups.reduce(
    (acc, group) => acc + group.items.length,
    0,
  );

  // Un solo fetch de pendientes para todo el shell (badges + chip del header).
  // El revenue-only admin no ve pendientes (su chip llevaría a memberships,
  // ruta que tampoco puede abrir).
  const pendingCountsRaw = usePendingCountsQuery();
  const pendingCounts = isRevenueOnlyAdmin ? EMPTY_PENDING_COUNTS : pendingCountsRaw;

  // Guard central para el rol acotado `{ role: "admin", approved: true }`:
  // solo puede estar en el dashboard de Revenue. Cualquier otra ruta admin
  // (incluido el home /admin y accesos directos por URL) redirige ahí. Cubre
  // TODAS las rutas admin porque cuelgan de este único layout.
  const guardLocation = useLocation();
  const revenueOnlyRedirect =
    isRevenueOnlyAdmin && !isPathAllowedForRevenueOnlyAdmin(guardLocation.pathname);

  return (
    <HeroUIProvider navigate={(path) => void navigate(path)}>
      <PendingCountsContext.Provider value={pendingCounts}>
      <div className="admin-app-shell dark min-h-[100dvh] bg-[#070708]">
        <SidebarProvider defaultOpen={defaultOpen}>

          {/* ── Sidebar ──────────────────────────────────────────────────── */}
          <Sidebar
            collapsible="icon"
            className="border-r border-white/[0.06] [&_[data-sidebar=sidebar]]:bg-black [&_[data-sidebar=sidebar]]:backdrop-blur-none"
          >
            {/* Header: avatar + nombre */}
            <SidebarHeader className="border-b border-white/[0.06] pb-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] md:pt-3">
              <div className="flex items-center gap-2.5 px-1">
                {/* Avatar Cloudinary con face-crop */}
                <div className="relative h-9 w-9 shrink-0">
                  {!avatarError ? (
                    <img
                      src={CLOUDINARY_AVATAR_URL}
                      alt={BRAND_PERSON_NAME || "Admin"}
                      onError={() => setAvatarError(true)}
                      className="h-9 w-9 rounded-full object-cover ring-1 ring-white/[0.14] shadow-md shadow-black/40"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.08] text-[13px] font-semibold text-white ring-1 ring-white/[0.14]">
                      {initial}
                    </div>
                  )}
                  {/* Punto verde de "online" */}
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-black bg-emerald-400 shadow-[0_0_0_1px_rgba(52,211,153,0.35)]" />
                </div>

                <div className="min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-600">
                    Admin
                  </p>
                  <p className="truncate font-ios-display text-[14px] font-semibold leading-tight tracking-tight text-zinc-50">
                    {BRAND_PERSON_NAME || "Panel"}
                  </p>
                </div>
              </div>
              <div className="mt-2.5 space-y-2 px-1 group-data-[collapsible=icon]:hidden">
                <SidebarInput
                  value={sidebarQuery}
                  onChange={(e) => setSidebarQuery(e.target.value)}
                  placeholder="Filtrar módulos..."
                  className="h-9 rounded-xl border-white/[0.08] bg-white/[0.045] text-zinc-200 placeholder:text-zinc-500"
                />
                <div className="flex items-center justify-between text-[10.5px] text-zinc-500">
                  <span>{totalVisibleModules} módulos</span>
                  <span className="admin-linear-pill px-1.5 py-0 text-[9px] text-zinc-400">
                    Buscar · ⌘K
                  </span>
                </div>
              </div>
            </SidebarHeader>

            {/* Nav groups */}
            <SidebarContent className="gap-1 px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]">
              {filteredGroups.length === 0 ? (
                <div className="px-2 py-4 text-center text-[12px] text-zinc-500">
                  Sin módulos para "{sidebarQuery}".
                </div>
              ) : (
                filteredGroups.map((group) => {
                  const orderedPaths = group.items.map((item) => item.to);
                  return (
                    <SidebarGroup key={group.label} className="py-1.5">
                      <SidebarGroupLabel className="mb-1.5 flex items-center justify-between px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
                        {group.label}
                        <span className="font-mono text-[9px] text-zinc-700">{group.items.length}</span>
                      </SidebarGroupLabel>
                      <SidebarGroupContent>
                        <DndContext
                          sensors={dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleGroupDragEnd(group.label, orderedPaths)}
                        >
                          <SortableContext
                            items={orderedPaths}
                            strategy={verticalListSortingStrategy}
                            disabled={dndDisabled}
                          >
                            <SidebarMenu className="gap-1">
                              {group.items.map((item) => (
                                <SortableNavRow key={item.to} id={item.to} disabled={dndDisabled}>
                                  {item.children ? (
                                    <NavItemWithChildren
                                      item={
                                        item as AdminSidebarNavItem & {
                                          children: NonNullable<AdminSidebarNavItem["children"]>;
                                        }
                                      }
                                    />
                                  ) : (
                                    <NavItemLeaf item={item} />
                                  )}
                                </SortableNavRow>
                              ))}
                            </SidebarMenu>
                          </SortableContext>
                        </DndContext>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  );
                })
              )}
            </SidebarContent>

            {/* Footer: site link + user */}
            <SidebarFooter className="border-t border-white/[0.06] px-2 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-3">
              <SidebarMenu>
                {hasCustomOrder && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={resetSidebarOrder}
                      tooltip="Restablecer orden del menú"
                      className="rounded-xl text-zinc-600 hover:bg-white/[0.05] hover:text-zinc-300"
                    >
                      <RotateCcw aria-hidden className="h-3.5 w-3.5" />
                      <span className="text-[12px]">Restablecer orden</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip="Ver sitio público"
                    className="rounded-xl text-zinc-500 hover:bg-white/[0.05] hover:text-zinc-200"
                  >
                    <a href="/" target="_blank" rel="noopener noreferrer">
                      <ExternalLink aria-hidden className="h-4 w-4" />
                      <span className="text-[13px]">Ver sitio público</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>

              {/* User Clerk */}
              <div className="mt-1 flex items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-white/[0.04] group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                <UserButton afterSignOutUrl="/" />
                <span className="text-[12px] font-medium text-zinc-500 group-data-[collapsible=icon]:hidden">
                  Cuenta
                </span>
              </div>
            </SidebarFooter>

            <SidebarRail className="bg-transparent hover:bg-white/[0.02]" />
          </Sidebar>

          {/* ── Main content ─────────────────────────────────────────────── */}
          <SidebarInset className="admin-page min-w-0 bg-[#070708] pb-[env(safe-area-inset-bottom,0px)] text-zinc-100">
            <AdminHeader onOpenPalette={() => setPaletteOpen(true)} />

            {/*
              Single content frame for the whole admin.
              Pages must use AdminPageFrame — do NOT re-nest max-w-* / admin-page.
            */}
            <div className="mx-auto w-full max-w-7xl px-4 pb-28 pt-5 sm:px-6 sm:pb-10 lg:px-8">
              {revenueOnlyRedirect ? (
                <Navigate to={REVENUE_ONLY_ADMIN_HOME} replace />
              ) : (
                <Outlet />
              )}
            </div>
          </SidebarInset>
          <AdminMobileDock items={mobileDockItems} onOpenPalette={() => setPaletteOpen(true)} />

          <AdminCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </SidebarProvider>
      </div>
      </PendingCountsContext.Provider>
    </HeroUIProvider>
  );
}
